import React from 'react';
import './BulkActionsBar.scss';
import CheckMarkFilled from '../../assets/images/check_outline_large.svg';
import CancelMarkFilled from '../../assets/images/cancel_outline_large.svg';

interface BulkActionsBarProps {
    /** Number of items currently selected */
    selectedCount: number;
    /** Handler for Confirm All action */
    onConfirmAll: () => void;
    /** Handler for Reject All action */
    onRejectAll: () => void;
    /** Handler for Clear Selection */
    onClearSelection: () => void;
    /** Handler for Undo last action */
    onUndo?: () => void;
    /** Whether undo is available */
    canUndo: boolean;

    undoCount: number;
    /** Whether actions are disabled (e.g., during processing) */
    disabled?: boolean;
}

/**
 * Floating action bar that appears when findings are selected,
 * providing bulk Confirm/Reject operations.
 */
function BulkActionsBar(props: BulkActionsBarProps) {
    const {
        selectedCount,
        onConfirmAll,
        onRejectAll,
        onClearSelection,
        onUndo,
        canUndo,
        undoCount = 0,
        disabled = false
    } = props;

    const undoOnlyMode = selectedCount === 0 && canUndo;

    if (selectedCount === 0 && !canUndo) {
        return null;
    }

    return (
        <div className="bulk-actions-bar" role="toolbar" aria-label="Bulk Actions">
            <div className="bulk-actions-content">
                <div className="bulk-actions-info">
                   { selectedCount > 0 && <span className="selection-count">
                        {selectedCount} {selectedCount === 1 ? 'finding' : 'findings'} selected
                    </span>}
                    { undoCount > 0 && selectedCount === 0 && (<span className="selection-count">
                        {undoCount} {undoCount === 1 ? 'finding' : 'findings'} to undo
                    </span>)}
                </div>

                <div className="bulk-actions-buttons">
                    <button
                        className="bulk-action-btn bulk-confirm-btn"
                        onClick={onConfirmAll}
                        disabled={disabled}
                        title={`Confirm ${selectedCount} selected ${selectedCount === 1 ? 'finding' : 'findings'}`}
                        type="button"
                    >
                        <img src={CheckMarkFilled} alt="" />
                        <span>Confirm All</span>
                    </button>

                    <button
                        className="bulk-action-btn bulk-reject-btn"
                        onClick={onRejectAll}
                        disabled={disabled}
                        title={`Reject ${selectedCount} selected ${selectedCount === 1 ? 'finding' : 'findings'}`}
                        type="button"
                    >
                        <img src={CancelMarkFilled} alt="" />
                        <span>Reject All</span>
                    </button>

                    {canUndo && onUndo && (
                        <button
                            className="bulk-action-btn bulk-undo-btn"
                            onClick={onUndo}
                            disabled={selectedCount > 0}
                            title="Undo last bulk action"
                            type="button"
                        >
                            <i className="fa fa-undo" aria-hidden="true"></i>
                            <span>Undo</span>
                        </button>
                    )}

                    <button
                        className="bulk-action-btn bulk-clear-btn"
                        onClick={onClearSelection}
                        disabled={disabled}
                        title="Clear selection"
                        type="button"
                    >
                        <i className="fa fa-times" aria-hidden="true"></i>
                        <span>Clear</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

export default BulkActionsBar;